import { useEffect, useState } from "react";

export type ConsentCategories = {
  necessary: true;
  analytics: boolean;
};

export type ConsentRecord = ConsentCategories & {
  decidedAt: string;
};

const STORAGE_KEY = "anexya-consent";
const CONSENT_UPDATED_EVENT = "anexya:consent-updated";
const CONSENT_OPEN_EVENT = "anexya:consent-open";

export function getConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.decidedAt !== "string") {
      return null;
    }
    return {
      necessary: true,
      analytics: parsed.analytics,
      decidedAt: parsed.decidedAt,
    };
  } catch {
    return null;
  }
}

export function setConsent(choice: { analytics: boolean }): ConsentRecord {
  const record: ConsentRecord = {
    necessary: true,
    analytics: choice.analytics,
    decidedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent<ConsentRecord>(CONSENT_UPDATED_EVENT, { detail: record }));
  return record;
}

export function clearConsent() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent<null>(CONSENT_UPDATED_EVENT, { detail: null }));
}

export function openConsentBanner() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

export function useConsent() {
  const [consent, setConsentState] = useState<ConsentRecord | null>(() => getConsent());

  useEffect(() => {
    const onUpdate = (e: Event) => {
      const detail = (e as CustomEvent<ConsentRecord | null>).detail;
      setConsentState(detail ?? null);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setConsentState(getConsent());
    };
    window.addEventListener(CONSENT_UPDATED_EVENT, onUpdate);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onUpdate);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return consent;
}

export const CONSENT_EVENTS = {
  updated: CONSENT_UPDATED_EVENT,
  open: CONSENT_OPEN_EVENT,
} as const;
